import type { CompletedComponent } from '../../agentInsightsUtils';
import type { ActivityKind, ActivityStep, ThoughtProcess, ToolClass } from '../conversation/activityGrouping';
import {
  commandGroupKind,
  commandGroupLabel,
  commandGroupToolClass,
  groupIntoThoughtProcesses,
  groupStepsIntoActivities,
} from '../conversation/activityGrouping';

export interface ComponentSlot {
  kind: 'component';
  index: number;
}

export interface ActivitySlot {
  kind: 'activity';
  id: string;
  label: string;
  activityKind: ActivityKind;
  errorCount: number;

  indices: number[];
}

export interface CommandsSlot {
  kind: 'commands';
  id: string;
  label: string;
  toolClass?: ToolClass;
  sameKind: boolean;
  toolCount: number;
  errorCount: number;
  hasError: boolean;
  activities: ActivitySlot[];
}

export interface ThoughtSlot {
  kind: 'thought';
  id: string;
  thought: ThoughtProcess;

  items: Array<CommandsSlot | ComponentSlot>;
}

export type RenderSlot = ComponentSlot | ThoughtSlot;

const KIND_BY_TYPE: Record<CompletedComponent['type'], ActivityStep['kind']> = {
  user: 'user',
  text: 'assistant',
  error: 'error',
  tool: 'tool',
};

const readCommand = (toolInputs: unknown): string | undefined => {
  if (!toolInputs || typeof toolInputs !== 'object' || !('command' in toolInputs)) {
    return undefined;
  }
  const command = toolInputs.command;
  if (typeof command === 'string' && command.trim()) {
    return command;
  }
  return undefined;
};

const serialize = (value: unknown): string | undefined => {
  if (value == null) {
    return undefined;
  }
  if (typeof value === 'string') {
    return value;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return undefined;
  }
};

export const planCompletedComponents = (
  components: CompletedComponent[],
  isGroupable: (component: CompletedComponent) => boolean,
): RenderSlot[] => {
  const steps: ActivityStep[] = components.map((component, index) => {
    const groupable = component.type === 'tool' && isGroupable(component);
    return {
      id: String(index),
      kind: component.type === 'tool' && !groupable ? 'system' : KIND_BY_TYPE[component.type],
      title: component.toolName ?? '',
      preview: readCommand(component.toolInputs),
      args: serialize(component.toolInputs),

      content: component.type === 'text' ? (serialize(component.content) ?? '') : undefined,
      timingKnown: false,
    };
  });

  const slots: RenderSlot[] = [];
  for (const top of groupIntoThoughtProcesses(groupStepsIntoActivities(steps))) {
    if (top.kind === 'step') {
      slots.push({ kind: 'component', index: Number(top.step.id) });
      continue;
    }
    slots.push({
      kind: 'thought',
      id: top.id,
      thought: top,
      items: top.items.map((item) =>
        item.kind === 'step'
          ? { kind: 'component' as const, index: Number(item.step.id) }
          : {
              kind: 'commands' as const,
              id: item.id,
              label: commandGroupLabel(item),
              toolClass: commandGroupToolClass(item),
              sameKind: commandGroupKind(item) !== undefined,
              toolCount: item.toolCount,
              errorCount: item.errorCount,
              hasError: item.hasError,
              activities: item.activities.map((activity) => ({
                kind: 'activity' as const,
                id: activity.id,
                label: activity.label,
                activityKind: activity.activityKind,
                errorCount: activity.errorCount,
                indices: activity.steps.map((step) => Number(step.id)),
              })),
            },
      ),
    });
  }
  return slots;
};
